/* global HTMLDivElement */

import * as React from "react";
import { makeStyles, Spinner } from "@fluentui/react-components";
import Markdown from "markdown-to-jsx";
import { AnswerWithImages } from "@/components/shared/AnswerWithImages";
import { AnswerImage } from "@/types";

type CardContentProps = {
  question: string;
  enhancedAnswer: string;
  images?: AnswerImage[];
  isLoading: boolean;
  showSpinner: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
};

export const CardContent: React.FC<CardContentProps> = ({
  question,
  enhancedAnswer,
  images,
  isLoading,
  showSpinner,
  contentRef,
}) => {
  const styles = useStyles();

  const markdownOptions = {
    overrides: {
      table: { props: { className: styles.table } },
      td: { props: { className: styles.td } },
      th: { props: { className: styles.th } },
      a: { props: { target: "_blank", rel: "noopener noreferrer" } },
    },
  };

  return (
    <>
      <div>
        <div className={styles.label}>Question:</div>
        <div className={styles.question}>{question}</div>
      </div>

      {!isLoading && (
        <div>
          <div className={styles.label}>Answer:</div>
          <div ref={contentRef}>
            {images && images.length > 0 ? (
              <AnswerWithImages
                answer={enhancedAnswer}
                images={images}
                className={styles.answer}
                markdownOptions={markdownOptions}
              />
            ) : (
              <Markdown className={styles.answer} options={markdownOptions}>
                {enhancedAnswer}
              </Markdown>
            )}
          </div>
        </div>
      )}

      {isLoading && (
        <div className={styles.loadingOverlay}>
          {showSpinner && <Spinner size="medium" />}
        </div>
      )}
    </>
  );
};

const useStyles = makeStyles({
  label: {
    paddingTop: "8px",
    fontSize: "12px",
    fontWeight: "bold",
  },
  question: {
    fontSize: "12px",
    fontStyle: "italic",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    wordBreak: "break-word",
  },
  answer: {
    fontSize: "12px",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    wordBreak: "break-word",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    borderSpacing: "0",
  },
  th: {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#f4f4f4",
    fontWeight: "bold",
    fontSize: "12px",
  },
  td: {
    border: "1px solid #ddd",
    padding: "8px",
    fontSize: "12px",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
});
